// User Types
export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
}

// Notification Types
export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

// Case Types
export interface Case {
  id: string;
  caseId?: string; // Backend uses caseId
  name: string;
  description?: string;
  status: 'Draft' | 'Processing' | 'Completed'; // Backend status values
  active: boolean; // Soft delete flag
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  // Three-phase workflow tracking
  currentPhase?: 'Discovery' | 'Analysis' | 'Design' | 'Completed';
  // Per-phase status — backend (CaseResponse in models/case.py) emits these as
  // PhaseStatus enums ('Not Started' | 'In Progress' | 'Completed').  Used by
  // PhaseBreadcrumb to color the chips per phase.
  discoveryPhaseStatus?: 'Not Started' | 'In Progress' | 'Completed';
  analysisPhaseStatus?: 'Not Started' | 'In Progress' | 'Completed';
  designPhaseStatus?: 'Not Started' | 'In Progress' | 'Completed';
  // Company snapshot metadata
  companyId?: string;
  companyName?: string;
  snapshotVersion?: string;
  hasNewData?: boolean;
  businessDomain?: BusinessDomain;
  systemLandscape?: SystemLandscape;
  dataFiles?: DataFile[];
  executiveNeeds?: ExecutiveNeeds;
  outputs?: CaseOutputs;
}

export interface BusinessDomain {
  businessDomainId: string;
  caseId: string;
  description: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface SystemLandscape {
  systems: EnterpriseSystem[];
  relationships: SystemRelationship[];
}

export interface EnterpriseSystem {
  id: string;
  name: string;
  type: string;
  description?: string;
  integrationMethods?: string[];
}

export interface SystemRelationship {
  sourceId: string;
  targetId: string;
  type: string;
  description?: string;
}

export interface DataFile {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  status: 'uploading' | 'uploaded' | 'processing' | 'processed' | 'error';
  preview?: DataPreview;
}

export interface DataPreview {
  schema: Record<string, string>;
  sampleRows: any[];
  rowCount: number;
  qualityScore?: number;
}

export interface ExecutiveNeeds {
  stakeholders: Stakeholder[];
  businessQuestions: string[];
  kpis: KPI[];
}

export interface Stakeholder {
  id: string;
  name: string;
  role: string;
  department?: string;
}

export interface KPI {
  id: string;
  name: string;
  description: string;
  category: string;
}

// Output Types
export interface CaseOutputs {
  schema?: Neo4jSchema;
  loaders?: DataLoaders;
  analytics?: AnalyticsPack;
  deployment?: DeploymentKit;
}

export interface Neo4jSchema {
  version: string | number; // Can be string ("1.0") or number (1) for schema version
  nodes: NodeDefinition[];
  relationships: RelationshipDefinition[];
  constraints: ConstraintDefinition[];
  indexes: IndexDefinition[];
  ddl: string;
  diagram?: string;
  qualityReport?: SchemaQualityReport;
  // Versioning fields
  schemaVersion?: number; // Explicit version number
  isActive?: boolean;
  parentVersion?: number;
  refinementReason?: string;
  createdAt?: string;
  completedAt?: string;
}

export interface NodeDefinition {
  label: string;
  category?: string;
  properties: PropertyDefinition[];
  description?: string;
}

export interface RelationshipDefinition {
  type: string;
  sourceLabel: string;
  targetLabel: string;
  from?: string;
  to?: string;
  properties: PropertyDefinition[];
  description?: string;
}

export interface PropertyDefinition {
  name: string;
  type: string;
  required: boolean;
  description?: string;
}

export interface ConstraintDefinition {
  name: string;
  type: 'UNIQUE' | 'EXISTS' | 'NODE_KEY';
  label: string;
  properties: string[];
}

export interface IndexDefinition {
  name: string;
  label: string;
  properties: string[];
  type?: string;
}

export interface SchemaQualityReport {
  score: number;
  issues: QualityIssue[];
  recommendations: string[];
}

export interface QualityIssue {
  severity: 'error' | 'warning' | 'info';
  message: string;
  location?: string;
}

export interface DataLoaders {
  contracts: LoaderContract[];
  cypherQueries: string[];
  apiEndpoints?: APIEndpoint[];
}

export interface LoaderContract {
  id: string;
  name: string;
  description: string;
  sourceFile: string;
  targetNode: string;
  mappings: FieldMapping[];
}

export interface FieldMapping {
  sourceField: string;
  targetProperty: string;
  transformation?: string;
}

export interface APIEndpoint {
  method: string;
  path: string;
  description: string;
  requestSchema?: any;
  responseSchema?: any;
}

export interface AnalyticsPack {
  queries: AnalyticsQuery[];
  dashboards?: Dashboard[];
  reports?: Report[];
}

export interface AnalyticsQuery {
  id: string;
  name: string;
  description: string;
  cypher: string;
  category: string;
  stakeholders: string[];
  kpis: string[];
}

export interface Dashboard {
  id: string;
  name: string;
  description: string;
  widgets: Widget[];
}

export interface Widget {
  id: string;
  type: string;
  title: string;
  queryId: string;
}

export interface Report {
  id: string;
  name: string;
  description: string;
  format: string;
  queries: string[];
}

export interface DeploymentKit {
  dockerConfig?: string;
  envTemplate?: string;
  setupInstructions?: string;
  documentation?: string;
}

// AI Processing Types
export interface ProcessingStatus {
  stage: 'research' | 'schema' | 'loaders' | 'analytics' | 'complete' | 'error';
  progress: number;
  currentActivity: string;
  estimatedTimeRemaining?: number;
  logs: ProcessingLog[];
}

export interface ProcessingLog {
  timestamp: string;
  level: 'info' | 'warning' | 'error';
  message: string;
}

// Chat Types
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  attachments?: ChatAttachment[];
}

export interface ChatAttachment {
  type: 'code' | 'diff' | 'diagram';
  content: any;
}

// API Response Types
export interface APIResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// Schema Evaluation Types
export interface ExecutiveQuestionFinding {
  question: string;
  stakeholder: string;
  supported: 'full' | 'partial' | 'none';
  reasoning: string;
  recommendations: string[];
  requiredNodes: string[];
  requiredRelationships: string[];
  missingElements: string[];
  queryComplexity?: 'simple' | 'moderate' | 'complex';
}

export interface ExecutiveQuestionsAnalysis {
  totalQuestions: number;
  fullySupported: number;
  partiallySupported: number;
  notSupported: number;
  findings: ExecutiveQuestionFinding[];
}

export interface BusinessContextAnalysis {
  domainAlignment: 'excellent' | 'good' | 'fair' | 'poor';
  findings: string[];
  recommendations: string[];
  missingDomainConcepts: string[];
}

export interface TechnicalAnalysis {
  indexCoverage: number;
  constraintCoverage: number;
  relationshipQuality: 'excellent' | 'good' | 'fair' | 'poor';
  findings: string[];
  recommendations: string[];
}

export interface SchemaEvaluation {
  evaluationId: string;
  schemaId: string;
  schemaVersion?: number; // Schema version this evaluation is based on
  caseId: string;
  version: number;
  isActive: boolean;
  overallScore: number;
  executiveQuestionsScore: number;
  businessContextScore: number;
  technicalQualityScore: number;
  executiveQuestionsAnalysis: ExecutiveQuestionsAnalysis;
  businessContextAnalysis: BusinessContextAnalysis;
  technicalAnalysis: TechnicalAnalysis;
  criticalImprovements: string[];
  evaluatedAt: string;
  evaluatedBy: string;
  refinementGenerated: boolean;
}

export interface SchemaEvaluationResponse {
  evaluation: SchemaEvaluation;
}

// Schema Versioning Types
export interface SchemaVersion {
  version: number;
  status: 'in_progress' | 'completed' | 'failed';
  isActive: boolean;
  parentVersion?: number;
  refinementReason?: string;
  qualityScore?: number;
  createdAt: string;
  completedAt?: string;
  nodeCount: number;
  relationshipCount: number;
  constraintCount: number;
  indexCount: number;
}

export interface SchemaVersionsResponse {
  caseId: string;
  versions: SchemaVersion[];
}

// Annual Report Types
export interface AnnualReportSummary {
  report: {
    reportId: string;
    companyName: string;
    fiscalYear: string;
    reportType: string;
    documentSource: string;
    processedAt: string;
  };
  entityCount: number;
  relationshipCount: number;
  entityTypes: string[];
  topEntities: {
    id: string;
    name: string;
    entityType: string;
    mentionCount: number;
  }[];
}

export interface FinancialOverview {
  companyMetrics: {
    name: string;
    revenue?: string;
    revenueUnit?: string;
    employees?: string;
    marketCap?: string;
    marketCapUnit?: string;
  };
  incomeStatement: FinancialMetric[];
  balanceSheet: FinancialMetric[];
  costBreakdown: FinancialMetric[];
  keyMetrics: FinancialMetric[];
}

export interface FinancialMetric {
  id: string;
  name: string;
  value?: string;
  unit?: string;
  period?: string;
  mentionCount?: number;
  description?: string;
}

export interface RevenueBreakdown {
  nodes: SankeyNode[];
  links: SankeyLink[];
  products: ProductEntity[];
  businessUnits: BusinessUnitEntity[];
  revenueStreams: any[];
}

export interface SankeyNode {
  id: number;
  name: string;
  category?: string;
}

export interface SankeyLink {
  source: number;
  target: number;
  value: number;
  label?: string;
}

export interface ProductEntity {
  id: string;
  name: string;
  revenueContribution?: number;
  description?: string;
}

export interface BusinessUnitEntity {
  id: string;
  name: string;
  revenue?: string | number;
  description?: string;
}

export interface SupplyChainVisualization {
  nodes: NetworkNode[];
  edges: NetworkEdge[];
  supplyChain: {
    suppliers: SupplyChainEntity[];
    materials: SupplyChainEntity[];
    logisticsPartners: SupplyChainEntity[];
  };
}

export interface NetworkNode {
  id: string;
  label: string;
  type: string;
  size: number;
  color: string;
}

export interface NetworkEdge {
  from: string;
  to: string;
  label: string;
  arrows?: string;
}

export interface SupplyChainEntity {
  id: string;
  name: string;
  entityType?: string;
}

export interface ValueChainVisualization {
  categories: ValueChainCategory[];
  total: number;
}

export interface ValueChainCategory {
  name: string;
  count: number;
  entities: {
    id: string;
    name: string;
  }[];
}

export interface RiskAnalysis {
  risks: RiskFactor[];
  riskMatrix: {
    severity: string;
    likelihood: string;
    risks: string[];
  }[];
}

export interface RiskFactor {
  id: string;
  name: string;
  severity?: string;
  likelihood?: string;
  description?: string;
}

export interface GraphData {
  nodes: GraphNode[];
  relationships: GraphRelationship[];
}

export interface GraphNode {
  id: string;
  label: string;
  type: string;
  properties: Record<string, any>;
}

export interface GraphRelationship {
  id: string;
  type: string;
  sourceId: string;
  targetId: string;
  properties: Record<string, any>;
}

// Business Model Canvas Types
export enum BusinessModelType {
  SAAS = 'SaaS',
  MARKETPLACE = 'Marketplace',
  ECOMMERCE = 'E-Commerce',
  SUBSCRIPTION = 'Subscription',
  FREEMIUM = 'Freemium',
  TRANSACTIONAL = 'Transactional',
  LICENSING = 'Licensing',
  ADVERTISING = 'Advertising',
  ASSET_HEAVY = 'Asset-Heavy',
  PLATFORM = 'Platform',
  HYBRID = 'Hybrid'
}

export enum CustomerSegmentType {
  B2C = 'B2C',
  B2B = 'B2B',
  B2G = 'B2G'
}

export enum DistributionType {
  DIRECT_SALES = 'Direct Sales',
  ONLINE = 'Online',
  RETAIL = 'Retail',
  DISTRIBUTOR = 'Distributor',
  PARTNER = 'Partner',
  MARKETPLACE = 'Marketplace',
  OEM = 'OEM'
}

export enum RevenueType {
  SUBSCRIPTION = 'Subscription',
  USAGE_BASED = 'Usage-Based',
  LICENSE = 'License',
  TRANSACTION = 'Transaction',
  ADVERTISING = 'Advertising',
  SERVICE = 'Service',
  PRODUCT_SALES = 'Product Sales',
  FREEMIUM = 'Freemium'
}

export enum ResourceType {
  PHYSICAL = 'Physical',
  INTELLECTUAL = 'Intellectual',
  HUMAN = 'Human',
  FINANCIAL = 'Financial'
}

export enum DataSource {
  XBRL = 'XBRL',
  LLM_ITEM1 = 'LLM-Item1',
  LLM_MDA = 'LLM-MD&A',
  LLM_NOTES = 'LLM-Notes',
  HYBRID = 'Hybrid'
}

export interface ProductService {
  product_id: string;
  name: string;
  category: string;
  description?: string;
  is_core_offering: boolean;
  target_segments?: string[];
  data_source: DataSource;
  confidence_score: number;
  xbrl_tag?: string;
}

export interface CustomerSegment {
  segment_id: string;
  name: string;
  segment_type: CustomerSegmentType;
  description?: string;
  size_estimate?: string;
  geographic_focus?: string[];
  is_primary_segment: boolean;
  data_source: DataSource;
  confidence_score: number;
}

export interface DistributionChannel {
  channel_id: string;
  name: string;
  channel_type: DistributionType;
  description?: string;
  geographic_scope?: string[];
  is_primary_channel: boolean;
  data_source: DataSource;
  confidence_score: number;
}

export interface RevenueStream {
  stream_id: string;
  name: string;
  revenue_type: RevenueType;
  description?: string;
  annual_revenue?: number;
  is_recurring: boolean;
  pricing_model?: string;
  data_source: DataSource;
  confidence_score: number;
  xbrl_tag?: string;
}

export interface CostStructure {
  cost_id: string;
  fiscal_period?: string;

  // Operating costs
  cost_of_revenue?: number;
  research_development?: number;
  sales_marketing?: number;
  general_administrative?: number;
  capital_expenditures?: number;

  // Calculated metrics
  gross_margin?: number;
  operating_margin?: number;
  capital_intensity?: number;

  // Cost classification
  fixed_costs_percentage?: number;
  variable_costs_percentage?: number;

  data_source: DataSource;
  confidence_score: number;
}

export interface KeyResource {
  resource_id: string;
  name: string;
  resource_type: ResourceType;
  description?: string;
  strategic_importance: 'Critical' | 'High' | 'Medium' | 'Low';
  data_source: DataSource;
  confidence_score: number;
}

export interface ValueProposition {
  proposition_id: string;
  headline?: string;
  description: string;
  unique_selling_points: string[];
  customer_pain_points_addressed?: string[];
  data_source: DataSource;
  confidence_score: number;
}

export interface CompetitivePosition {
  position_id: string;
  market_position?: string;
  key_competitors?: string[];
  competitive_advantages: string[];
  market_threats?: string[];
  data_source: DataSource;
  confidence_score: number;
}

export interface BusinessStrategy {
  strategy_id: string;
  strategic_focus: string[];
  growth_drivers: string[];
  expansion_plans?: string[];
  risk_mitigation_strategies?: string[];
  data_source: DataSource;
  confidence_score: number;
}

export interface BusinessModelCanvas {
  canvas_id: string;
  company_id: string;
  report_id: string;
  business_model_type: BusinessModelType;

  // Core components
  products_services: ProductService[];
  customer_segments: CustomerSegment[];
  distribution_channels: DistributionChannel[];
  revenue_streams: RevenueStream[];
  cost_structure?: CostStructure;
  key_resources: KeyResource[];

  // Strategic components
  value_proposition?: ValueProposition;
  competitive_position?: CompetitivePosition;
  business_strategy?: BusinessStrategy;

  // Quality metrics
  data_completeness: number;
  xbrl_coverage: number;
  llm_coverage: number;
  overall_confidence: number;

  extraction_timestamp: string;
}

// Three-Phase Workflow Types
export * from './threePhase';
